/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@compiled/react';

import RandomizeIcon from '@atlaskit/icon-lab/core/randomize';
import BoardIcon from '@atlaskit/icon/core/board';
import DatabaseIcon from '@atlaskit/icon/core/database';
import GridIcon from '@atlaskit/icon/core/grid';
import TableIcon from '@atlaskit/icon/core/table';
import {
	BUILTIN_TOOLBAR_KEYS,
	createEditorToolbarActions,
	type NativeEmbedParameterValues,
} from '@atlaskit/native-embeds-common';
import { allNativeEmbedCoreFeatures } from '@atlassian/native-embeds-core';
import LoadingOverlay from '@atlassian/native-embeds-core/loading-overlay';
import {
	type Experience,
	type ExperienceManifest,
	experienceManifestId,
	type ExperienceManifestId,
	type LoadingComponentProps,
	type NativeEmbedChildCommand,
	type NativeEmbedFeature,
} from '@atlassian/native-embeds-core/types';

export const DB_EXPERIENCE_MANIFEST_ID: ExperienceManifestId = experienceManifestId('database');

const isDatabaseUrl = (url: string): boolean => {
	return url.match(/\/wiki\/spaces\/~?[\d\w]+\/database\/\d+(\?.*)?$/) !== null;
};

const dbIcons = [DatabaseIcon, TableIcon, GridIcon, BoardIcon];

/**
 * Experience manifest for a DB embed.
 * Register with getNativeEmbedFacade().registerExperienceManifest(DBExperienceManifest),
 * then call getNativeEmbedFacade().createExperienceForUrl(url) to obtain an Experience for EmbedFrameUI.
 */
export const DBExperienceManifest = {
	id: DB_EXPERIENCE_MANIFEST_ID,
	isUrlMatch: isDatabaseUrl,
	isActive: () => true,
	handleChildCommands: (_experience: Experience, _commands: NativeEmbedChildCommand): void => {},
	parameterDefaults: {
		alwaysShowTitle: true,
	},
	supportedFeatures: new Set<NativeEmbedFeature>(allNativeEmbedCoreFeatures),
	transformUrl: (url: string, _isFullScreen?: boolean): string => {
		const urlObj = new URL(url);
		urlObj.searchParams.set('presentationMode', 'nativeEmbedEditorPlugin');
		return urlObj.toString();
	},
	uiConfig: {
		editorToolbarActions: createEditorToolbarActions({
			customActions: {
				remix: {
					type: 'button',
					key: 'Remix',
					handlerKey: 'remix',
					// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-object
					label: 'Remix',
					icon: RandomizeIcon,
					showTitle: true,
				},
			},
			items: [
				BUILTIN_TOOLBAR_KEYS.ASK_ROVO,
				'remix',
				BUILTIN_TOOLBAR_KEYS.SEPARATOR,
				BUILTIN_TOOLBAR_KEYS.EMBED,
				BUILTIN_TOOLBAR_KEYS.SEPARATOR,
				BUILTIN_TOOLBAR_KEYS.ALIGNMENT,
				BUILTIN_TOOLBAR_KEYS.SEPARATOR,
				BUILTIN_TOOLBAR_KEYS.OPEN_IN_NEW_WINDOW,
				BUILTIN_TOOLBAR_KEYS.EDIT_URL,
				BUILTIN_TOOLBAR_KEYS.SEPARATOR,
			],
			moreItems: [
				BUILTIN_TOOLBAR_KEYS.ALWAYS_SHOW_TITLE,
				BUILTIN_TOOLBAR_KEYS.COPY_LINK,
				BUILTIN_TOOLBAR_KEYS.DELETE,
			],
		}),
		editorToolbarHandlers: {
			onAskRovoClick: (parameters: NativeEmbedParameterValues) => {
				if (parameters.url) {
					const url = new URL(parameters.url);
					url.searchParams.set('rovoChatTriggerOpen', 'true');
					window.open(url.toString(), '_blank', 'noopener,noreferrer');
				}
			},
		},
		renderLoadingComponent: (props: LoadingComponentProps) => (
			<LoadingOverlay
				animationDisabled={props.animationDisabled}
				icons={dbIcons}
				testId={props.testId}
			/>
		),
		titleIcon: DatabaseIcon,
		appearance: 'minimal',
		showHeader: false,
		showBorder: false,
	},
} as const satisfies ExperienceManifest;
